// FILE: AddDNScheduleModal.js
// CHANGE: New modal for Delivery Note Scheduler (copied from AddGRNScheduleModal and adjusted)

import React, { useEffect, useRef, useState } from "react";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
  Row,
} from "../../../../../../components/Component";
import { Alert, Spinner } from "reactstrap";
import TextInput from "../../../../../components/inputs/TextInput";
import { messageToast } from "../../../../../../utils/Utils";
import * as app_consts from "../../../../../../constants/react_app_consts";
import { IS_EMPTY_OR_WHITESPACE } from "../../../../../../utils/RegexUtils";
import moment from "moment";
import { ExportToCsv } from "export-to-csv";

const initialState = { name: "" };

// CHANGE: DN schedule expected keys (we will adjust later if backend requires different)
const BACKEND_KEYS = [
  "loading_order_ref_no",
  "horse_reg",
  "trailer_reg_one",
  "trailer_reg_two",
  "driver",
  "transporter",
  "offload_point",
  "tonnage", // optional
  "date_loaded",
];

const csv_headers = [...BACKEND_KEYS];

const normalizeHeader = (h) =>
  (h || "")
    .replace(/"/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/\./g, "");

const HEADER_ALIASES = {
  trailer_reg: "trailer_reg_one",
  destination: "offload_point",
  load_reference: "loading_order_ref_no",
  load_ref: "loading_order_ref_no",
  loading_order_ref: "loading_order_ref_no",
};

const MIN_REQUIRED_HEADERS = [
  "loading_order_ref_no",
  "horse_reg",
  "trailer_reg_one",
  "driver",
  "transporter",
  "offload_point",
  "date_loaded",
];

const pickBackendFields = (row) => ({
  loading_order_ref_no: row?.loading_order_ref_no ?? "",
  horse_reg: row?.horse_reg ?? "",
  trailer_reg_one: row?.trailer_reg_one ?? "",
  trailer_reg_two: row?.trailer_reg_two ?? "",
  driver: row?.driver ?? "",
  transporter: row?.transporter ?? "",
  offload_point: row?.offload_point ?? "",
  tonnage: row?.tonnage ?? "",
  date_loaded: row?.date_loaded ?? "",
});

const AddDNScheduleModal = ({ successFunction, setModalLoading }) => {
  const [loading, setLoading] = useState(false);
  const [errorVal, setError] = useState("");
  const [formData, setFormData] = useState(initialState);
  const [loading_message, setLoadingMessage] = useState("");

  const [failed_imports, setFailedImports] = useState([]);
  const [successImports, setSuccessImports] = useState([]);
  const [imported_data, setImportedData] = useState([]);
  const [imported_data_list, setImportedDataList] = useState([]);

  const [successImportsPerPage] = useState(10);
  const [currentSuccessImportsPage, setSuccessImportsPage] = useState(1);

  const [failedImportsPerPage] = useState(10);
  const [currentFailedImportsPage, setSuccessFailedImportsPage] = useState(1);

  const fileInputRef = useRef(null);

  const failedImportsPaginate = (pageNumber) => setSuccessFailedImportsPage(pageNumber);
  const successImportsPaginate = (pageNumber) => setSuccessImportsPage(pageNumber);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // CHANGE: No backend yet — so we just block submit for now
  const handlerAddDnSchedule = () => {
    setError("");
    messageToast("Delivery Note Scheduler backend is not added yet.", app_consts.FAIL);
    setModalLoading(false);
  };

  const handleCsvUpload = (event) => {
    setError("");
    const file = event?.target?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    setLoading(true);
    setLoadingMessage("Reading CSV File");

    reader.onload = (e) => {
      const csv_data = e?.target?.result;
      convertToJson(csv_data);
    };

    reader.onerror = () => {
      setLoading(false);
      setLoadingMessage("");
      messageToast("Error reading file", app_consts.FAIL);
    };

    reader.readAsText(file);
  };

  const convertToJson = (csv_data) => {
    const lines = (csv_data || "")
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    if (lines.length < 2) {
      messageToast("CSV file has no data rows.", app_consts.FAIL);
      setLoading(false);
      setLoadingMessage("");
      return;
    }

    const incomingHeaders = lines[0].split(",").map((h) => h.trim()).map(normalizeHeader);
    const mappedHeaders = incomingHeaders.map((h) => HEADER_ALIASES[h] || h);

    const missing = MIN_REQUIRED_HEADERS.filter((h) => !mappedHeaders.includes(h));
    if (missing.length > 0) {
      messageToast(`Invalid headers. Missing: ${missing.join(", ")}`, app_consts.FAIL);
      setLoading(false);
      setLoadingMessage("");
      return;
    }

    const result = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map((v) => v.trim());
      if (values.every((v) => v === "")) continue;

      const row = {};
      mappedHeaders.forEach((h, idx) => {
        row[h] = values[idx] ?? "";
      });

      result.push(pickBackendFields(row));
    }

    if (result.length === 0) {
      messageToast("No Data has been Found.", app_consts.FAIL);
      setLoading(false);
      setLoadingMessage("");
      return;
    }

    setFailedImports([]);
    setSuccessImports([]);
    setImportedData([]);
    setImportedDataList([]);

    setImportedData(result);
    setImportedDataList(result);
  };

  useEffect(() => {
    if (imported_data?.length > 0) {
      validate_uploaded_csv_data(imported_data[0]);
    } else {
      setLoadingMessage("");
      checkImportedDataList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imported_data]);

  const checkImportedDataList = () => {
    if (
      failed_imports.length + successImports.length === imported_data_list.length &&
      imported_data_list.length !== 0
    ) {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const validate_uploaded_csv_data = (sent_data) => {
    const copy_sent_data = sent_data;
    const errors = [];

    const fields = [
      { key: "loading_order_ref_no", label: "Loading Order Ref No is Required" },
      { key: "horse_reg", label: "Horse Reg is Required" },
      { key: "driver", label: "Driver is Required" },
      { key: "transporter", label: "Transporter is Required" },
      { key: "offload_point", label: "Offload Point is Required" },
      { key: "date_loaded", label: "Date Loaded is Required" },
    ];

    fields.forEach(({ key, label }) => {
      const isInvalid = IS_EMPTY_OR_WHITESPACE.test(copy_sent_data?.[key]);
      if (isInvalid) errors.push(label);
    });

    const normalize = (v) => (v ?? "").toString().trim().toLowerCase();
    const duplicate = imported_data?.filter((data) => {
      return (
        normalize(data?.loading_order_ref_no) === normalize(copy_sent_data?.loading_order_ref_no) &&
        normalize(data?.horse_reg) === normalize(copy_sent_data?.horse_reg) &&
        normalize(data?.driver) === normalize(copy_sent_data?.driver) &&
        normalize(data?.transporter) === normalize(copy_sent_data?.transporter) &&
        normalize(data?.offload_point) === normalize(copy_sent_data?.offload_point) &&
        normalize(data?.date_loaded) === normalize(copy_sent_data?.date_loaded)
      );
    });

    if (duplicate?.length > 1) errors.push("Duplicate Entry");

    if (errors.length > 0) {
      setFailedImports((prev) => [...prev, { ...copy_sent_data, errors }]);
    } else {
      setSuccessImports((prev) => [...prev, { ...copy_sent_data }]);
    }

    const updated_import_data = imported_data.filter((_, index) => index !== 0);
    setImportedData(updated_import_data);
  };

  const handleReUploadClick = () => {
    setFailedImports([]);
    setSuccessImports([]);
    setImportedData([]);
    setImportedDataList([]);
    setLoadingMessage("");
    setError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const indexOfLastSuccessImport = currentSuccessImportsPage * successImportsPerPage;
  const indexOfFirstSuccessImport = indexOfLastSuccessImport - successImportsPerPage;
  const currentSuccessImports = successImports.slice(indexOfFirstSuccessImport, indexOfLastSuccessImport);

  const indexOfLastFailedImport = currentFailedImportsPage * failedImportsPerPage;
  const indexOfFirstFailedImport = indexOfLastFailedImport - failedImportsPerPage;
  const currentFailedImports = failed_imports.slice(indexOfFirstFailedImport, indexOfLastFailedImport);

  const export_to_csv = () => {
    const cur_dt = moment().format("DD-MM-YYYY h:mm:ss a");

    const options = {
      fieldSeparator: ",",
      quoteStrings: '"',
      showLabels: true,
      showTitle: false,
      useTextFile: false,
      useBom: true,
      filename: `United African Grain Delivery Note Schedule CSV Template (as of ${cur_dt})`,
      useKeysAsHeaders: false,
      headers: csv_headers,
    };

    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv([{}]);
  };

  return (
    <>
      {errorVal && (
        <div className="mb-3 w-100">
          <Alert color="danger" className="alert-icon">
            <Icon name="alert-circle" /> {errorVal}{" "}
          </Alert>
        </div>
      )}

      <BlockDes className="text-soft fs-16px">
        <p>
          To Download CSV Template,{" "}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              export_to_csv();
            }}
            style={{ textDecoration: "underline" }}
            className="text-underline"
          >
            Click Here
          </a>
        </p>
      </BlockDes>

      <form onSubmit={(e) => e.preventDefault()}>
        <Row className="g-4">
          <TextInput
            label="Name"
            required
            type="text"
            id_val="name"
            maxlength="100"
            value={formData.name}
            colSize="2"
            name="name"
            handleChange={handleChange}
          />

          <Col xl="12" className="d-flex justify-content-start">
            <Button
              onClick={handlerAddDnSchedule}
              size="lg"
              type="submit"
              color="primary"
              disabled={loading ? loading : !(formData?.name && successImports?.length > 0)}
            >
              Add Delivery Note Schedule
            </Button>
          </Col>
        </Row>

        {loading ? (
          <div className="p-5 d-flex align-center justify-center d-flex flex-column">
            <span style={{ fontSize: 16, marginBottom: 2 }} className="text-soft">
              {loading_message}
            </span>
            <Spinner />
          </div>
        ) : (
          <>
            {failed_imports.length + successImports.length === imported_data_list.length &&
            imported_data_list.length !== 0 ? (
              <>
                <Block>
                  <BlockBetween className="mb-4">
                    <BlockHeadContent />
                    <BlockHeadContent>
                      <div className="card-inner">
                        <Button color="primary" size="sm" onClick={handleReUploadClick}>
                          Re-Upload CSV
                        </Button>
                      </div>
                    </BlockHeadContent>
                  </BlockBetween>
                </Block>

                {/* Valid Data */}
                <div className="card-inner">
                  <Block>
                    <BlockBetween className="mb-4">
                      <BlockHeadContent>
                        <BlockTitle tag="h5">Valid Data (To Be Imported)</BlockTitle>
                      </BlockHeadContent>
                    </BlockBetween>

                    {successImports?.length > 0 ? (
                      <>
                        <div className="nk-tb-list is-separate is-medium mb-3">
                          <DataTableHead className="nk-tb-item">
                            <DataTableRow><span className="sub-text">Loading Order Ref</span></DataTableRow>
                            <DataTableRow><span className="sub-text">Horse Reg</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Trailer Reg 1</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Trailer Reg 2</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Driver</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Transporter</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Offload Point</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Tonnage (Optional)</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Date Loaded</span></DataTableRow>
                          </DataTableHead>

                          {currentSuccessImports.map((item, idx) => (
                            <DataTableItem key={idx}>
                              <DataTableRow><span className="tb-sub">{item?.loading_order_ref_no || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.horse_reg || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.trailer_reg_one || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.trailer_reg_two || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.driver || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.transporter || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.offload_point || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.tonnage || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.date_loaded || "--"}</span></DataTableRow>
                            </DataTableItem>
                          ))}
                        </div>

                        <div className="mt-3">
                          <PaginationComponent
                            itemPerPage={successImportsPerPage}
                            totalItems={successImports.length}
                            paginate={successImportsPaginate}
                            currentPage={currentSuccessImportsPage}
                          />
                        </div>
                      </>
                    ) : (
                      <span style={{ fontSize: 16 }} className="text-soft">
                        No Valid Data Found
                      </span>
                    )}
                  </Block>
                </div>

                <hr />

                {/* Invalid Data */}
                <div className="card-inner">
                  <Block>
                    <BlockBetween className="mb-4">
                      <BlockHeadContent>
                        <BlockTitle tag="h5">Invalid Data (Not To Be Imported)</BlockTitle>
                      </BlockHeadContent>
                    </BlockBetween>

                    {failed_imports?.length > 0 ? (
                      <>
                        <div className="nk-tb-list is-separate is-medium mb-3">
                          <DataTableHead className="nk-tb-item">
                            <DataTableRow><span className="sub-text">Loading Order Ref</span></DataTableRow>
                            <DataTableRow><span className="sub-text">Horse Reg</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Trailer Reg 1</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Trailer Reg 2</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Driver</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Transporter</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Offload Point</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Tonnage (Optional)</span></DataTableRow>
                            <DataTableRow size="lg"><span className="sub-text">Date Loaded</span></DataTableRow>
                          </DataTableHead>

                          {currentFailedImports.map((item, idx) => (
                            <DataTableItem key={idx}>
                              <DataTableRow><span className="tb-sub">{item?.loading_order_ref_no || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.horse_reg || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.trailer_reg_one || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.trailer_reg_two || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.driver || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.transporter || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.offload_point || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.tonnage || "--"}</span></DataTableRow>
                              <DataTableRow><span className="tb-sub">{item?.date_loaded || "--"}</span></DataTableRow>
                            </DataTableItem>
                          ))}
                        </div>

                        <div className="mt-3">
                          <PaginationComponent
                            itemPerPage={failedImportsPerPage}
                            totalItems={failed_imports.length}
                            paginate={failedImportsPaginate}
                            currentPage={currentFailedImportsPage}
                          />
                        </div>
                      </>
                    ) : (
                      <span style={{ fontSize: 16 }} className="text-soft">
                        No Invalid Data Found
                      </span>
                    )}
                  </Block>
                </div>

                <input
                  type="file"
                  id="dnCsvUpload"
                  accept=".csv,text/csv"
                  style={{ display: "none" }}
                  onChange={handleCsvUpload}
                  ref={fileInputRef}
                />
              </>
            ) : (
              <div
                className="product-thumb mt-4"
                style={{
                  height: "250px",
                  width: "100%",
                  position: "relative",
                  border: "1px dashed #ccc",
                  backgroundColor: "#f9f9f9",
                  overflow: "hidden",
                }}
              >
                <label
                  htmlFor="dnCsvUpload"
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    color: "#777",
                    fontSize: "16px",
                    fontWeight: "bold",
                    fontFamily: "Arial, sans-serif",
                    cursor: "pointer",
                    textAlign: "center",
                  }}
                >
                  <p style={{ margin: 0 }}>Click to Upload CSV File</p>
                </label>

                <input
                  type="file"
                  id="dnCsvUpload"
                  accept=".csv,text/csv"
                  style={{ display: "none" }}
                  onChange={handleCsvUpload}
                  ref={fileInputRef}
                />
              </div>
            )}
          </>
        )}
      </form>
    </>
  );
};

export default AddDNScheduleModal;
